import React, { use, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  useTheme,
  Link,
  useMediaQuery,
} from '@mui/material';
import BreadcrumbNavbar from '../components/BreadcrumbNavbar';
import axios from 'axios';
import api from '../api/axios';
import { jwtDecode } from 'jwt-decode';
import { StyledTab, StyledTabs } from '../components/StyledComponents';
import { YellowBoxBottom } from './Home/Home';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ReactComponent as SunIcon } from '../assets/images/chart_su.svg';
import { ReactComponent as MoonIcon } from '../assets/images/chart_mo.svg';
import { ReactComponent as MarsIcon } from '../assets/images/chart_ma.svg';
import { ReactComponent as MercuryIcon } from '../assets/images/chart_me.svg';
import { ReactComponent as JupiterIcon } from '../assets/images/chart_ju.svg';
import { ReactComponent as VenusIcon } from '../assets/images/chart_ve.svg';
import { ReactComponent as SaturnIcon } from '../assets/images/chart_sa.svg';
import { ReactComponent as RahuIcon } from '../assets/images/chart_ra.svg';
import { ReactComponent as KetuIcon } from '../assets/images/chart_ke.svg';
import _, { set } from 'lodash';

const PLANET_CODE_2_ICON = {
  Su: <SunIcon />,
  Mo: <MoonIcon />,
  Ma: <MarsIcon />,
  Me: <MercuryIcon />,
  Ju: <JupiterIcon />,
  Ve: <VenusIcon />,
  Sa: <SaturnIcon />,
  Ra: <RahuIcon />,
  Ke: <KetuIcon />,
};

const ASCENDANT_SIGN_DESCRIPTIONS = {
  Aries:
    'Your outer personality and how other perceive you. <b>Aries</b> brings <b>Aggression Boldness and courage</b>',
  Taurus:
    'How you show up to the world & the impression you leave. <b>Taurus</b> brings <b>Stability, Patience & Determination</b>',
  Gemini:
    'How people see you and your outward vibe. <b>Gemini</b> brings <b>Curiosity, Adaptability, and Sociability</b>.',
  Cancer:
    'The way you appear to others and your natural aura <b>Cancer</b> brings <b>Sensitivity, Nurturing, and Protectiveness.</b>',
  Leo: 'The energy you radiate and how others notice you: <b>Leo</b> brings <b>Confidence, Charisma, and Leadership.</b>',
  Virgo:
    'The impression you give and the traits people notice <b>Virgo</b> brings <b>Practicality, Reliability, and Precision.</b>',
  Libra:
    'How you present yourself and the vibe others feel <b>Libra</b> brings <b>Charm, Diplomacy, and Sociability.</b>',
  Scorpio:
    'The aura you project and how others perceive you <b>Scorpio</b> brings <b>Intensity, Passion, and Mystery.</b>',
  Sagittarius:
    'The energy you share and the impression you leave on others <b>Sagittarius</b> brings <b>Optimism, Adventure and Honesty.</b>',
  Capricorn:
    'The way you carry yourself and how others see you <b>Capricorn</b> brings <b>Discipline, Ambition & Responsibility.</b>',
  Aquarius:
    'The vibe you radiate and the way people perceive you <b>Aquarius</b> brings <b>Innovation, Independence and Eccentricity.</b>',
  Pisces:
    'How your presence feels to those around you <b>Pisces</b> brings <b>Compassion, Imagination, and Intuition.</b>',
};

const RASI_SIGN_DESCRIPTIONS = {
  Aries:
    '<b>Aries</b> moon reflects fiery emotions, <b>bold</b> instincts, and a natural urge to lead with <b>courage</b> and <b>action.</b>',
  Taurus:
    'Grounded and nurturing at heart, <b>Taurus</b> moon reflects emotional <b>strength, patience</b>, and a <b>deep love</b> for comfort.',
  Gemini:
    'Adaptable and curious, <b>Gemini</b> moon reflects quick <b>emotions, sharp intellect</b> and a constant need for expression.',
  Cancer:
    '<b>Cancer</b> moon reflects nurturing emotions, empathy, and a strong need for security. Deeply <b>sensitive</b> and <b>intuitive</b>',
  Leo: 'The fire of your heart and spirit. <b>Leo</b> moon reveals <b>passionate emotions, creativity</b> and a strong desire for <b>recognition.</b>',
  Virgo:
    'The quiet rhythm of your inner world. <b>Virgo</b> moon reflects thoughtful <b>emotions, practicality</b> and a <b>deep need for order.</b>',
  Libra:
    'The balance within your heart. <b>Libra</b> moon reflects harmony in <b>emotions, charm</b> in connections, and a deep need for <b>partnership.</b>',
  Scorpio:
    'The depth of your inner soul. <b>Scorpio</b> moon reveals <b>intense emotions, magnetic</b> energy, and a profound need <b>for transformation.</b>',
  Sagittarius:
    'The fire of your inner journey. <b>Sagittarius</b> moon reflects adventurous emotions, <b>free-spirited</b> energy, and a deep need for truth.',
  Capricorn:
    'The anchor of your inner world. <b>Capricorn</b> moon reflects <b>disciplined emotions</b>, steady <b>energy</b> and a need for structure and achievement.',
  Aquarius:
    'The vision of your inner self. <b>Aquarius</b> moon reflects innovative emotions, <b>independent energy</b>, and a feeling <b>social freedom</b> and <b>unconventional</b> truth.',
  Pisces:
    'The depth of your inner world. <b>Pisces</b> moon reflects <b>compassionate emotions, intuitive energy</b> and a sense of spiritual understanding.',
};

const PlanetCard = ({ code, data, planetColor }) => {
  const { name, sign, number } = data;

  const numberStr = number
    ? number === 1
      ? '1st'
      : number === 2
        ? '2nd'
        : number === 3
          ? '3rd'
          : `${number}th`
    : '';
  const placement = sign
    ? `in ${sign} • ${numberStr} house`
    : 'Placement info unavailable';
  const icon = PLANET_CODE_2_ICON[code];
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 0,
        backgroundColor: '#F7F7F0',
        boxShadow: 'none',
        display: 'flex',
        alignItems: 'center',
        p: 2,
      }}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          p: 0,
          '&:last-child': { pb: 0 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 40,
              width: 40,
              mr: 2,
              fontSize: '1.5rem',
            }}
          >
            {icon}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: '500', color: '#454545' }}
            >
              {name}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: '500', color: '#909090', fontSize: '0.875rem' }}
            >
              {placement}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: '500',
              color: planetColor,
              fontSize: '1rem',
            }}
          >
            {code}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.5rem',
              color: '#909090',
            }}
          >
            Planet code
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const HoroscopeSection = ({ horoscopeData, isMobile }) => {
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const tabs = [
    { label: 'Daily', key: 'daily' },
    { label: 'Weekly', key: 'weekly' },
    { label: 'Monthly', key: 'monthly' },
  ];

  const currentHoroscope = horoscopeData[tabs[value].key];

  return (
    <Box sx={{ width: '100%', mt: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography sx={{ fontWeight: '500', fontSize: '45px', mb: '23px' }}>
          Your Horoscope
        </Typography>
        <Typography
          variant="h4"
          fontWeight={500}
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          Personalised cosmic guidance based on your unique birth chart and
          current planetary transits.
        </Typography>
      </Box>

      <Box sx={{ width: '100%', mb: 4 }}>
        <StyledTabs value={value} onChange={handleChange} centered>
          {tabs.map((tab) => (
            <StyledTab key={tab.key} label={tab.label} />
          ))}
        </StyledTabs>
      </Box>

      <Box display={'flex'} justifyContent={'center'}>
        <Card
          elevation={0}
          sx={{
            borderRadius: '6px',
            borderColor: '#909090',
            border: '1px solid',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: '760px',
            width: '100%',
            px: { xs: 2, sm: 4, md: 6 },
            pt: { xs: 1, sm: 1, md: 2 },
            pb: 0,
          }}
        >
          <CardContent sx={{ '&:last-child': { pb: 0 } }}>
            <Typography
              sx={{
                fontWeight: '500',
                mb: 2,
                textAlign: 'center',
                fontSize: '34px',
              }}
            >
              {currentHoroscope.title}
            </Typography>
            <Divider sx={{ mx: 2, my: 2 }} />
            <Typography
              variant="body1"
              sx={{ mb: 3, textAlign: isMobile ? 'left' : 'justify' }}
              dangerouslySetInnerHTML={{ __html: currentHoroscope.text }}
            >
              {/* {currentHoroscope.text} */}
            </Typography>
            <Box
              sx={{
                textAlign: 'center',
                mb: 0.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                width: '100%',
              }}
            >
              {/* <Typography
                variant="body1"
                bgcolor="#F7F7F0"
                color="#909090"
                sx={{ fontWeight: '500', height: '54px', justifyContent: 'center', alignItems: 'center', display: 'flex', width: '100%' }}
              >
                Lucky Color: {currentHoroscope.luckyColor} • Lucky Number: {currentHoroscope.luckyNumber}
              </Typography> */}
              <Typography
                variant="caption"
                sx={{
                  display: 'flex',
                  textAlign: 'center',
                  fontSize: '12px',
                  pt: 1,
                }}
              >
                {tabs[value].key === 'daily'
                  ? 'Date'
                  : tabs[value].key === 'weekly'
                    ? 'Week'
                    : 'Month'}
                : {currentHoroscope.lastUpdated}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

function ChartDetails() {
  const [lightSvg, setLightSvg] = useState('');
  const [darkSvg, setDarkSvg] = useState('');
  const [chartData, setChartData] = useState({});
  const [planetColors, setPlanetColors] = useState({});
  const [ascendant, setAscendant] = useState('');
  const [rasi, setRasi] = useState('');
  const theme = useTheme();

  const [formData, setFormData] = useState({
    name: '',
    dob: '',
    timeOfBirth: '',
    placeOfBirth: '',
  });

  const [defaultLat, defaultLng] = formData.placeOfBirth.split(',');

  const [placeLabel, setPlaceLabel] = useState('');

  const [userId, setUserId] = useState('');

  const [horoscopeData, setHoroscopeData] = useState({
    daily: {
      title: "Today's Guidance",
      text: '',
      luckyColor: 'Yellow',
      luckyNumber: '7',
      lastUpdated: '',
    },
    weekly: {
      title: 'Weekly Outlook',
      text: '',
      luckyColor: 'Green',
      luckyNumber: '9',
      lastUpdated: '',
    },
    monthly: {
      title: 'Monthly Forecast',
      text: '',
      luckyColor: 'Blue',
      luckyNumber: '3',
      lastUpdated: '',
    },
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const id = decoded.email;
        setUserId(id);
        axios
          .get(`${api}/user/details/${id}`)
          .then((res) => {
            if (res.data?.[0]) {
              setFormData({
                name: res.data[0].name || '',
                dob: res.data[0].dob || '',
                timeOfBirth: res.data[0].timeOfBirth || '',
                placeOfBirth: res.data[0].placeOfBirth || '',
              });
            }
          })
          .catch((err) => console.error('Failed to fetch user details:', err));
      } catch (err) {
        console.error('Invalid token:', err);
      }
    }
  }, []);

  useEffect(() => {
    const fetchDefaultLocation = async () => {
      if (defaultLat && defaultLng) {
        try {
          const res = await axios.get(`${api}/places/reverse-geocode`, {
            params: { lat: defaultLat, lng: defaultLng },
          });
          if (res.data) {
            const place = {
              label: res.data.label,
              place_id: res.data.place_id,
              lat: res.data.lat,
              lng: res.data.lng,
            };
            setPlaceLabel(place?.label ?? '');
          }
        } catch (err) {
          console.error('Reverse geocode failed:', err);
        }
      }
    };
    fetchDefaultLocation();
  }, [defaultLat, defaultLng]);

  const requestSent = useRef(false);
  useEffect(() => {
    // Fetch the SVG files
    if (requestSent.current) return; // already loaded
    requestSent.current = true;
    console.log('Fetching SVG files...');
    axios
      .post(
        `${api}/chart/get_chart_details`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      )
      .then((res) => {
        setLightSvg(res.data.lightSvg);
        setDarkSvg(res.data.darkSvg);
        setChartData(res.data.chartData);
        setPlanetColors(res.data.planetColors || {});
        setAscendant(res.data.ascendant || '');
        setRasi(res.data.rasi || '');
        const horoscope = res.data.horoscope || {};
        const newHoroscopeData = { ...horoscopeData };
        if (_.has(horoscope, 'daily')) {
          newHoroscopeData.daily.lastUpdated = horoscope.daily.date;
          newHoroscopeData.daily.text = horoscope.daily.horoscope;
        }
        if (_.has(horoscope, 'weekly')) {
          newHoroscopeData.weekly.lastUpdated = horoscope.weekly.week;
          newHoroscopeData.weekly.text = horoscope.weekly.horoscope;
        }
        if (_.has(horoscope, 'monthly')) {
          newHoroscopeData.monthly.lastUpdated = horoscope.monthly.month;
          newHoroscopeData.monthly.text = horoscope.monthly.horoscope;
        }
        console.log('SVG files and chart data fetched successfully', res.data);
      })
      .catch((err) => {
        console.error('Error fetching SVG files and chart data:', err);
        requestSent.current = false;
      });
  }, [requestSent.current]);

  const requestSentHoroscope = useRef(false);
  useEffect(() => {
    if (requestSentHoroscope.current) return; // already loaded
    requestSentHoroscope.current = true;
    console.log('Fetching Horoscope...');
    axios
      .post(
        `${api}/chart/get_horoscope`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      )
      .then((res) => {
        const horoscope = res.data || {};
        const newHoroscopeData = _.cloneDeep(horoscopeData);
        if (_.has(horoscope, 'daily')) {
          newHoroscopeData.daily.lastUpdated = horoscope.daily.date;
          newHoroscopeData.daily.text = horoscope.daily.horoscope;
        }
        if (_.has(horoscope, 'weekly')) {
          newHoroscopeData.weekly.lastUpdated = horoscope.weekly.week;
          newHoroscopeData.weekly.text = horoscope.weekly.horoscope;
        }
        if (_.has(horoscope, 'monthly')) {
          newHoroscopeData.monthly.lastUpdated = horoscope.monthly.month;
          newHoroscopeData.monthly.text = horoscope.monthly.horoscope;
        }
        setHoroscopeData(newHoroscopeData);
        console.log('Horoscope data fetched successfully', res.data);
      })
      .catch((err) => {
        console.error('Error fetching Horoscope data:', err);
        requestSentHoroscope.current = false;
      });
  }, [requestSentHoroscope.current]);

  const svgString = theme.palette.mode === 'dark' ? darkSvg : lightSvg;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <BreadcrumbNavbar />
      <Box sx={{ p: { xs: 2, md: 6 }, textAlign: 'center', width: '100%' }}>
        {/* Header */}
        <Typography
          variant="h1"
          sx={{ maxWidth: '740px', textAlign: 'center', mx: 'auto' }}
        >
          Your Vedic Astrology Chart Details
        </Typography>
        <Typography
          variant="h4"
          maxWidth="600px"
          mx="auto"
          mb="80px"
          fontWeight={500}
        >
          Discover your Rising and Moon signs, along with in-depth insights
          through the wisdom of Vedic Astrology
        </Typography>

        <Box
          sx={{
            position: 'relative',
            maxWidth: 360,
            mx: 'auto',
            mb: 6,
          }}
        >
          <Card
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: '#BABABA',
              backgroundColor: 'transparent',
              borderRadius: '8px',
            }}
          >
            <CardContent>
              <Typography paragraph>
                Date: <strong>{formData.dob}</strong>
              </Typography>
              <Typography paragraph>
                Time: <strong>{formData.timeOfBirth}</strong>
              </Typography>
              <Typography>
                Place: <strong>{placeLabel}</strong>
              </Typography>
            </CardContent>
          </Card>

          <Typography
            variant="h6"
            sx={{
              position: 'absolute',
              bottom: -15, // adjust gap below card
              right: 0,
              color: 'text.secondary',
            }}
          >
            Active Chart Details
          </Typography>
        </Box>

        {/* Main Content */}
        <Grid container spacing={0} justifyContent="center">
          {/* Birth Chart */}
          <Grid item size={{ xs: 13, md: 7 }}>
            <Typography variant="h2">Birth Chart (D1: Rasi)</Typography>
            <Typography variant="h4" fontWeight={500} mb={2}>
              Your Vedic Chart Blueprint at the moment of Birth
            </Typography>

            <Box
              sx={{
                width: '100%',
                maxWidth: 680,
                mx: 'auto',
                aspectRatio: '1 / 1',
                '& svg': {
                  width: '100%',
                  height: '100%',
                },
              }}
              dangerouslySetInnerHTML={{ __html: svgString }}
            />
          </Grid>

          {/* Key Signs Summary */}
          <Grid
            item
            size={{ xs: 11, md: 5 }}
            alignItems={'center'}
            display={'flex'}
            flexDirection={'column'}
          >
            <Typography variant="h2">Key Signs Summary</Typography>
            <Typography variant="h4" fontWeight={500} mb={2}>
              The most important signs shaping your personality
            </Typography>

            <Card
              variant="outlined"
              sx={{
                textAlign: 'center',
                mb: 2,
                border: '1px solid',
                borderRadius: '8px',
                borderColor: '#BABABA',
                backgroundColor: 'transparent',
                maxWidth: '460px',
                width: '100%',
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  sx={{ fontSize: '1.25rem', fontWeight: 500, mb: 1 }}
                >
                  Rising Sign (Ascendant){' '}
                  <Typography
                    component="span"
                    color="#D78100"
                    sx={{ fontSize: '1.25rem', fontWeight: 500 }}
                  >
                    {ascendant}
                  </Typography>
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  dangerouslySetInnerHTML={{
                    __html: ASCENDANT_SIGN_DESCRIPTIONS[ascendant],
                  }}
                />
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{
                textAlign: 'center',
                border: '1px solid',
                borderRadius: '8px',
                borderColor: '#BABABA',
                backgroundColor: 'transparent',
                width: '100%',
                maxWidth: '460px',
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  sx={{ fontSize: '1.25rem', fontWeight: 500, mb: 1 }}
                >
                  Moon Sign{' '}
                  <Typography
                    component="span"
                    color="#D78100"
                    sx={{ fontSize: '1.25rem', fontWeight: 500 }}
                  >
                    {rasi}
                  </Typography>
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  dangerouslySetInnerHTML={{
                    __html: RASI_SIGN_DESCRIPTIONS[rasi],
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 6, mt: '100px' }}>
            <Typography variant="h2">Planetary Placements</Typography>
            <Typography
              variant="h4"
              fontWeight={500}
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              The position of celestial bodies in your chart and their influence
              on different aspects of your life.
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {Object.keys(chartData).map((planet) => (
              <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={planet}>
                <PlanetCard
                  code={planet}
                  data={chartData[planet]}
                  planetColor={planetColors[planet]}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        <Divider sx={{ mx: 20 }} />
        <HoroscopeSection horoscopeData={horoscopeData} isMobile={isMobile} />
        <Box display={'flex'} justifyContent={'center'}>
          <YellowBoxBottom sx={{ maxWidth: '760px', width: '100%' }}>
            <Box sx={{ textAlign: 'center', padding: 2 }}>
              <Typography variant="h2" fontWeight={600}>
                💡 What Can You Ask Our AI?
              </Typography>
              <Typography>
                Wondering what mind-blowing questions you can ask our powerful
                Astrology AI? Don’t stay curious!
              </Typography>
              <Typography>
                👉 Dive into our
                <strong>
                  <Link
                    href="/what-to-ask"
                    rel="noopener noreferrer"
                    underline="hover"
                    color="secondary"
                    sx={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    “Idea What to Ask”{' '}
                    <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
                  </Link>
                </strong>{' '}
                <br />
                page and get inspired with the coolest ideas! ✨
              </Typography>
            </Box>
          </YellowBoxBottom>
        </Box>
      </Box>
    </>
  );
}

export default ChartDetails;
